import { Help } from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";
import Email from "@mui/icons-material/Email";
import ForumIcon from "@mui/icons-material/Forum";
import MinimizeIcon from "@mui/icons-material/Minimize";
import { Grid } from "@mui/material";
import { useContext, useEffect, useState } from "react";

import { HasbotContext, TechSupportType } from "../../context/hasBot-context";
import { useAiSmeChatConfig } from "../../hooks/useAiSmeChatConfig";
import { useAppState } from "../../hooks/useAppState";
import { createTelemetryEvent } from "../../telemetry/Telemetry";
import { telemetryChannelName } from "../../util/constants";
import { AiSparkleIcon } from "./AiSparkleIcon";
import { BetaBadge, StyledIconButton } from "./Common.elements";
import {
  ContentContainer,
  HoursText,
  PaperButton,
  PaperContainer,
  SectionText,
  SectionTitle,
  Title,
  TitleContainer,
} from "./HelpAndSupportMainPage.elements";

export function HelpAndSupportMainPage() {
  const [loading] = useState(false);

  const [zopimStatus, setZopimStatus] = useState({
    timestamp: "",
    status: "offline",
    hours: [],
  });

  const { course } = useAppState();
  const isDemoCourse = course?.courseType === "Demo";
  const { isLoading: isAiChatConfigLoading, enabled: isAiChatEnabled } = useAiSmeChatConfig();

  const {
    setTechSupportActive,
    setZopimChatActive,
    setShowDialog,
    openDialogBtnRef,
    setAiChatActive,
    zopimChatActive,
  } = useContext(HasbotContext);

  const dispatchHoursEvent = () => {
    const zopimHours = new Event("updateHours");
    document.dispatchEvent(zopimHours);
  };

  const handleEmailClick = (type: TechSupportType) => () => {
    setTechSupportActive(type);
  };

  const handleChatClick = () => {
    setZopimChatActive(true);
    dispatchHoursEvent();

    // Telemetry: Chat opened
    createTelemetryEvent({
      feature: "human sme chat",
      action: "opened",
    }).fire(telemetryChannelName);
  };

  const handleAiClick = () => {
    setAiChatActive(true);
  };

  const closeDialog = () => {
    // Telemetry: Chat closed (only if chat was active)
    if (zopimChatActive) {
      createTelemetryEvent({
        feature: "human sme chat",
        action: "closed",
      }).fire(telemetryChannelName);
    }

    setShowDialog(false);
    openDialogBtnRef.current?.focus();
    const endChat = new Event("endZopimChat");
    document.dispatchEvent(endChat);
    dispatchHoursEvent();
  };

  const minimizeDialog = () => {
    setShowDialog(false);
    openDialogBtnRef.current?.focus();
  };

  const handleHours = (schedule: string | null) => {
    if (schedule) {
      const newSchedule = JSON.parse(schedule);
      setZopimStatus(newSchedule);
    }
  };

  useEffect(() => {
    handleHours(localStorage.getItem("zopimSchedule"));
    window.addEventListener(
      "zopimSchedule",
      () => {
        handleHours(localStorage.getItem("zopim_schedule_update"));
      },
      false,
    );
  }, []);

  return (
    <>
      <ContentContainer>
        <TitleContainer container sx={{ justifyContent: "space-between", alignItems: "center" }}>
          <Grid
            container
            size={{ xs: 8 }}
            sx={{ justifyContent: "flex-start" }}
            alignItems="center"
            aria-live="assertive"
            gap={1}
          >
            <Help sx={{ color: theme => theme.custom.palette.white, width: 20, height: 20 }} />
            <Title>Help &amp; Support</Title>
          </Grid>
          <Grid sx={{ justifyContent: "flex-end" }}>
            <StyledIconButton
              id="minimize-help-dialog"
              aria-label="Minimize Help Dialog"
              size="small"
              onClick={minimizeDialog}
            >
              <MinimizeIcon fontSize="inherit" />
            </StyledIconButton>
            <StyledIconButton id="close-help-dialog" aria-label="Close Help Dialog" size="small" onClick={closeDialog}>
              <CloseIcon fontSize="inherit" />
            </StyledIconButton>
          </Grid>
        </TitleContainer>

        <Grid container direction="column" gap={0.5}>
          {/* SME Help */}
          <div>
            <PaperContainer id="lab-content-quiz-help-container">
              <SectionTitle>Lab, Content, or Quiz Help</SectionTitle>
              <SectionText>Our Subject Matter Experts (SMEs) are here to help you.</SectionText>
              <>
                <SectionText>Live chat available from:</SectionText>
                <HoursText>
                  {zopimStatus.hours?.map((hours: string) => (
                    <span key={hours}>{hours}</span>
                  ))}
                </HoursText>
              </>
              <SectionText>No time to chat? Email the SME team.</SectionText>
            </PaperContainer>
            <Grid>
              <PaperButton
                autoFocus={zopimStatus.status === "online"}
                id="ask-a-sme"
                startIcon={<Email />}
                onClick={handleEmailClick(TechSupportType.SME)}
                loading={loading}
                sx={{ width: "50%" }}
              >
                Email SME
              </PaperButton>
              <PaperButton
                autoFocus={zopimStatus.status === "online"}
                id="chat-with-us"
                startIcon={<ForumIcon />}
                onClick={handleChatClick}
                loading={loading}
                disabled={zopimStatus.status !== "online"}
                sx={{ width: "50%" }}
              >
                Chat with SME
              </PaperButton>
            </Grid>
          </div>

          {/* Get AI Support (if config loaded and enabled) */}
          {!isAiChatConfigLoading && isAiChatEnabled && (
            <div>
              <PaperContainer id="ai-support-container">
                <SectionTitle>Get AI Support</SectionTitle>
                <SectionText>
                  Get answers to your questions about labs or course content from an AI chatbot trained on the course
                  {isDemoCourse ? " (available in full courses only)" : ""}.
                </SectionText>
              </PaperContainer>
              <Grid>
                <PaperButton
                  autoFocus={zopimStatus.status === "online"}
                  id="ask-ai-question"
                  startIcon={<AiSparkleIcon style={{ transform: "scale(1.2)" }} />}
                  onClick={handleAiClick}
                  loading={loading}
                  disabled={isDemoCourse}
                  sx={{ width: "100%" }}
                >
                  Chat with AI <BetaBadge mode="light">BETA</BetaBadge>
                </PaperButton>
              </Grid>
            </div>
          )}

          {/* AI Support coming soon (if still loading or not enabled) */}
          {!isAiChatConfigLoading && !isAiChatEnabled && (
            <div>
              <PaperContainer id="ai-support-container">
                <SectionTitle>AI SME (Coming soon)</SectionTitle>
                <SectionText>
                  Your instant, contextual helper trained on the course content and ready to answer your questions will
                  be available in your course soon.
                </SectionText>
              </PaperContainer>
              <Grid>
                <PaperButton
                  autoFocus={zopimStatus.status === "online"}
                  id="ask-ai-question"
                  startIcon={<AiSparkleIcon style={{ transform: "scale(1.2)" }} />}
                  onClick={() => {}}
                  loading={false}
                  disabled={true}
                  sx={{ width: "100%" }}
                >
                  Chat with AI (Coming soon)
                </PaperButton>
              </Grid>
            </div>
          )}

          {/* Technical Support */}
          <div>
            <PaperContainer id="technical-support-container" sx={{ marginBottom: "0" }}>
              <SectionTitle>Customer Support</SectionTitle>
              <SectionText>
                Contact our Customer Support team for questions related to finding your VM(s)/ISO, VMWare licensing,
                Player or Mobile App issues, questions about your GIAC exam, scheduling or practice tests or other
                non-technical questions.
              </SectionText>
            </PaperContainer>
            <Grid>
              <PaperButton
                autoFocus={zopimStatus.status === "online"}
                id="ask-support-question"
                startIcon={<Email />}
                onClick={handleEmailClick(TechSupportType.CSS)}
                loading={loading}
                sx={{ width: "100%" }}
              >
                Email Support
              </PaperButton>
            </Grid>
          </div>
        </Grid>
      </ContentContainer>
    </>
  );
}
